import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from '@otp/ui';
import clsx from 'clsx';
import {
  WttpQuestionsQuery,
  WttpQuestionsDocument,
  WttpQuestionListFilterInput,
  SortingDirection,
  WttpQuestionStatus,
  Subjects,
  Actions,
  AppArea,
} from 'src/graphql/generated/graphql';
import { useQuery } from '@apollo/client';
import { WttpQuestionTable } from './table/table';
import { useTable } from 'src/hooks/use-table';
import { QuestionsFilter } from './filter';
import { CreateQuestionButton } from './create-question-button';
import { useAuthContext } from 'src/context/auth.context';
import { WttpQuestionAction } from 'src/components/wttp/questions/list/table/columns/actions/actions-modals';
import { FilterWrapper } from 'src/components/common/wrapper/filter/wrapper';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { EntityDetails } from './details/details';
import { Can } from 'src/utils/can';


const PAGINATION_LIMIT = 10;

enum TabType {
  Custom = 'custom',
  Global = 'global',
}

export const WttpQuestionList = () => {
  const { t } = useTranslation();
  const [totalCount, setTotalCount] = useState(0);
  const { user, isAdmin } = useAuthContext();

  const TABS = [
    { label: t('common.custom'), value: TabType.Custom },
    { label: t('common.global'), value: TabType.Global },
  ];

  const [activeTab, setActiveTab] = useState(
    isAdmin ? TabType.Global : TabType.Custom,
  );

  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);

  const {
    page,
    setPage,
    filter: filterData,
    onFilterChange,
    defaultFilter,
  } = useTable<
    WttpQuestionListFilterInput & { status?: WttpQuestionStatus & 'archived' },
    Record<any, any>
  >();

  const { status: filterStatus, ...filter } = filterData || {};
  const isArchivedFilter = filterStatus === 'archived';

  const onActionFinished = (action: WttpQuestionAction) => {
    if (!isAdmin && action === WttpQuestionAction.Clone) {
      setActiveTab(TabType.Custom);
    }
  };

  const { loading, data } = useQuery<WttpQuestionsQuery>(
    WttpQuestionsDocument,
    {
      variables: {
        filter: {
          ...filter,
          organizationId:
            activeTab === TabType.Custom ? user?.organizationId : null,
          statuses: isArchivedFilter
            ? undefined
            : filterStatus
            ? [filterStatus]
            : [WttpQuestionStatus.Active, WttpQuestionStatus.Draft],
          isActive: !isArchivedFilter,
        },
        sorting: {
          title: SortingDirection.Asc,
        },
        pagination: {
          limit: PAGINATION_LIMIT,
          offset: page * PAGINATION_LIMIT,
        },
      },
      onCompleted: (data) =>
        setTotalCount(data.wttpQuestions.pagination.totalCount),
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    },
  );

  const wttpQuestions = data?.wttpQuestions?.records || [];

  const onTabChange = (_: any, value: any) => {
    setActiveTab(value);
    setPage(0);
  };

  const records = data?.wttpQuestions?.records || [];

  const onRowClick = (_: any, { rowIndex }: { rowIndex: number }) => {
    setSelectedEntityId(records[rowIndex].id);
  };

  return (
    <Can I={Actions.Read} a={Subjects.Questions} appArea={AppArea.Wttp}>
      <FilterWrapper>
        <QuestionsFilter
          onSubmit={onFilterChange}
          defaultFilter={defaultFilter}
        />
      </FilterWrapper>

      <ContentWrapper>
        <TableWrapper>
          <div className={clsx('relative', { 'flex justify-end': isAdmin })}>
            {!isAdmin && (
              <Tabs tabs={TABS} onChange={onTabChange} value={activeTab} />
            )}
            <CreateQuestionButton position={isAdmin ? 'default' : 'fixed'} />
          </div>
          <WttpQuestionTable
            data={wttpQuestions}
            className="mt-4"
            loading={loading}
            options={{
              page,
              count: totalCount,
              sort: true,
              rowsPerPage: PAGINATION_LIMIT,
              onRowClick,
              onChangePage: setPage,
              enableNestedDataAccess: '.',
            }}
            onActionFinished={onActionFinished}
          />
        </TableWrapper>
        {selectedEntityId && (
          <EntityDetails
            entityId={selectedEntityId}
            refetchQueries={[WttpQuestionsDocument]}
            onClose={() => setSelectedEntityId(null)}
          />
        )}
      </ContentWrapper>
    </Can>
  );
};
